import React, { useState, useEffect, useMemo } from "react";

import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CheckCircle,
  Download,
  FileText,
  Loader2,
  Pencil,
  Save,
  Upload,
  Users,
  UserRound,
  X,
  Building2,
  Database,
} from "lucide-react";

import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { AdminUser, fetchAdminUsers } from "../../Store/Reducers/MasterSlice";
import {
  AccountTeamCsvValidateResult,
  bulkUpload,
  clearBulkUpload,
  fetchAccountsWithSponsors,
  fetchApprovalRequests,
  fetchUploadStats,
  validateAccountTeamCsv,
} from "../../Store/Reducers/OpsSlice";
import { fetchVendorAdminGrades } from "../../Store/Reducers/VendorAdminSlice";
import { regionOptions } from "../../Utils/Constants/Constants";
import {
  ACCOUNT_TEAM_ALLOWED_ROLES,
  ACCOUNT_TEAM_STATIC_SAMPLES,
  buildTeamMemberSampleRow,
  CUSTOMER_TEAM_STATIC_SAMPLES,
  CUSTOMER_TEAM_ALLOWED_ROLES,
  getTeamRequiredColumns,
  getTeamTemplateColumns,
  TeamUploadType,
} from "../../Utils/Constants/DataUploadConstants";
import {
  normalizeCustomerTeamRole,
  normalizeRoleName,
  CUSTOMER_TEAM_ROLE_VALIDATION_MESSAGE,
  ROLE_DISPLAY_NAMES,
} from "../../Utils/Constants/RoleConstants";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../../Utils/Helper/LocalStorage";
import Button from "../Button";
import CustomCheckbox from "../CheckCircle";
import Input from "../Input";
import Modal from "../Modal";
import SearchInput from "../Search/SearchInput";

type ExOpsUploadType = "executives" | "accounts" | "composite";

interface DataUploadManagerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Ex-ops (default) shows executives/accounts/composite; team shows account/customer team uploads. */
  mode?: "ex-ops" | "team";
  /** Pre-selected upload type when the modal opens. */
  initialActiveType?: ExOpsUploadType | TeamUploadType;
  /** Active sub-tab API filter — used to refresh approvals after a successful upload. */
  approvalUploadType?: string;
}

type UploadType =
  | "executives"
  | "accounts"
  | "composite"
  | "upload_account_team_members"
  | "upload_customer_team_members";

const TEAM_UPLOAD_TYPES: UploadType[] = [
  "upload_account_team_members",
  "upload_customer_team_members",
];

const isTeamUploadType = (type: UploadType): boolean =>
  TEAM_UPLOAD_TYPES.includes(type);

const truncateAccountId = (id: string): string =>
  id.length > 12 ? `${id.slice(0, 8)}...` : id;

const DATA_HUB_ROLE_LABELS = [
  "executive leader",
  "c level",
  "sponsor",
] as const;

// Preferred column order for preview table by upload type (whitelist; only these columns shown)
const PREVIEW_COLUMN_ORDER: Record<UploadType, string[]> = {
  executives: [
    "name",
    "title",
    "email",
    "phone",
    "city",
    "country",
    "region",
    "specialization",
    "grade",
    "languages",
    "max_accounts",
    "certifications",
    "role",
  ],
  accounts: [
    "name",
    "industry",
    "tier",
    "churn_risk",
    "revenue",
    "employees",
    "city",
    "country",
    "contact_person",
    "contact_email",
    "contact_phone",
    "renewal_date",
  ],
  composite: [
    "executive_name",
    "executive_title",
    "executive_email",
    "executive_phone",
    "executive_region",
    "executive_specialization",
    "executive_grade",
    "executive_max_accounts",
    "executive_certifications",
    "executive_languages",
    "account_name",
    "account_industry",
    "account_tier",
    "account_churn_risk",
    "account_revenue",
    "account_employees",
    "account_city",
    "account_country",
    "account_contact_person",
    "account_contact_email",
    "account_contact_phone",
    "account_renewal_date",
    "relationship_type",
  ],
  upload_account_team_members: [...getTeamTemplateColumns()],
  upload_customer_team_members: [...getTeamTemplateColumns()],
};

const EX_OPS_UPLOAD_TYPES: UploadType[] = [
  "executives",
  "accounts",
  "composite",
];

const DataUploadManager: React.FC<DataUploadManagerProps> = ({
  isOpen,
  onClose,
  mode = "ex-ops",
  initialActiveType,
  approvalUploadType,
}) => {
  const [selectedType, setSelectedType] = useState<UploadType>("executives");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showSelectApprover, setShowSelectApprover] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [batchName, setBatchName] = useState("");
  /** Snapshot of the auto-generated default; if `batchName` still matches, backend gets null. */
  const [defaultBatchName, setDefaultBatchName] = useState("");
  const [selectedApprover, setSelectedApprover] = useState<string | null>(null);
  const [approverSearchQuery, setApproverSearchQuery] = useState("");
  const [uploadError, setUploadError] = useState<string | string[] | null>(
    null
  );

  const uploadErrorMessages = useMemo(() => {
    if (!uploadError) return [];
    return Array.isArray(uploadError) ? uploadError : [uploadError];
  }, [uploadError]);
  const [editedCells, setEditedCells] = useState<Set<string>>(new Set());
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [hoveredCell, setHoveredCell] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Array<{ rowIndex: number; field: string; message: string }>
  >([]);
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [accountSearchQuery, setAccountSearchQuery] = useState("");
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const submittedBy =
    loggedInUser?.email ||
    loggedInUser?.fullName ||
    loggedInUser?.name ||
    "N/A";

  // Redux hooks for admin users
  const dispatch = useAppDispatch();
  const {
    items: adminUsers,
    loading: approversLoading,
    error: approversError,
  } = useAppSelector(state => state.master.adminUsers);
  const { grades } = useAppSelector(state => state.vendorAdmin);
  const { isUploading: isBulkUploading, success: bulkUploadSuccess } =
    useAppSelector(state => state.ops.bulkUpload);
  const { loading: accountTeamCsvValidating } = useAppSelector(
    state => state.ops.accountTeamCsvValidate
  );
  const {
    data: accountsData,
    loading: accountsLoading,
    error: accountsError,
  } = useAppSelector(state => state.ops.accounts);

  const uploadAccounts = accountsData?.items ?? [];
  const filteredUploadAccounts = useMemo(() => {
    const q = accountSearchQuery.trim().toLowerCase();
    if (!q) return uploadAccounts;
    return uploadAccounts.filter(account =>
      account.name.toLowerCase().includes(q)
    );
  }, [uploadAccounts, accountSearchQuery]);

  const selectedAccountsForUpload = useMemo(
    () =>
      uploadAccounts.filter(account =>
        selectedAccountIds.includes(account.customer_id)
      ),
    [uploadAccounts, selectedAccountIds]
  );

  /** Team template can be prefilled when accounts are selected; static template otherwise */
  const hasSelectedAccountsForTemplate =
    isTeamUploadType(selectedType) && selectedAccountIds.length > 0;

  // When modal opens, pre-select upload type from Overview tab (e.g. Sponsors → Executives)
  useEffect(() => {
    if (!isOpen) return;
    if (initialActiveType) {
      setSelectedType(initialActiveType);
      return;
    }
    setSelectedType(
      mode === "team" ? "upload_account_team_members" : "executives"
    );
  }, [isOpen, initialActiveType, mode]);

  // Fetch approvers (admin users) when modal opens — same as Add Executive / Add Account
  useEffect(() => {
    if (isOpen && adminUsers.length === 0 && !approversLoading) {
      dispatch(fetchAdminUsers());
    }
  }, [isOpen, dispatch, adminUsers.length, approversLoading]);

  // Fetch vendor grades (for dynamic capacity_limits / max_accounts) when needed
  useEffect(() => {
    if (
      isOpen &&
      (selectedType === "executives" || selectedType === "composite") &&
      !grades
    ) {
      dispatch(fetchVendorAdminGrades());
    }
  }, [isOpen, selectedType, grades, dispatch]);

  // Fetch accounts for team upload account selection
  useEffect(() => {
    if (
      isOpen &&
      isTeamUploadType(selectedType) &&
      !accountsLoading &&
      uploadAccounts.length === 0
    ) {
      dispatch(fetchAccountsWithSponsors({}));
    }
  }, [isOpen, selectedType, accountsLoading, uploadAccounts.length, dispatch]);

  // Clear bulk upload state when modal closes
  useEffect(() => {
    if (!isOpen) {
      dispatch(clearBulkUpload());
      setSelectedAccountIds([]);
      setAccountSearchQuery("");
    }
  }, [isOpen, dispatch]);

  // Handle successful upload: refresh data, clear state, close modal
  useEffect(() => {
    if (bulkUploadSuccess) {
      dispatch(fetchApprovalRequests(approvalUploadType));
      dispatch(fetchUploadStats());
      onClose();
      dispatch(clearBulkUpload());
    }
  }, [bulkUploadSuccess, onClose, dispatch, approvalUploadType]);

  // Clear approver search when leaving approver step
  useEffect(() => {
    if (!showSelectApprover) {
      setApproverSearchQuery("");
    }
  }, [showSelectApprover]);

  const mapAdminUserToApprover = (user: AdminUser) => {
    const roleName =
      typeof user.role === "string" ? user.role : user.role?.name || "";
    const normalizedRole = normalizeRoleName(roleName);
    const roleLabel =
      ROLE_DISPLAY_NAMES[normalizedRole as keyof typeof ROLE_DISPLAY_NAMES] ||
      roleName.replace(/_/g, " ") ||
      roleName;
    return {
      id: user.user_id,
      name: user.full_name,
      role: roleLabel,
      title: user.position || "N/A",
      email: user.email,
    };
  };

  const availableApprovers = adminUsers.map(mapAdminUserToApprover);

  // Map grade (number or name) → capacity_limits for dynamic max_accounts
  const gradeCapacityMap = React.useMemo(() => {
    const map: Record<string, number> = {};
    if (grades?.items && Array.isArray(grades.items)) {
      grades.items.forEach((item: any) => {
        const capacity = item.capacity_limits ?? 0;
        if (item.grade !== undefined && item.grade !== null) {
          map[String(item.grade).trim()] = capacity;
        }
        if (item.grade_name) {
          map[String(item.grade_name).trim()] = capacity;
        }
      });
    }
    return map;
  }, [grades]);

  // Apply capacity_limits → max_accounts fields based on grade for current upload type
  const applyCapacityLimitsToData = React.useCallback(
    (data: any[], type: UploadType): any[] => {
      if (!data || data.length === 0) return data;
      if (!gradeCapacityMap || Object.keys(gradeCapacityMap).length === 0) {
        return data;
      }

      return data.map(row => {
        const updatedRow = { ...row };

        if (type === "executives") {
          const rawGrade = row.grade ?? row.Grade;
          const gradeKey =
            rawGrade !== undefined && rawGrade !== null
              ? String(rawGrade).trim()
              : "";
          if (gradeKey && gradeCapacityMap[gradeKey] !== undefined) {
            const expected = gradeCapacityMap[gradeKey];
            const current = String(row.max_accounts ?? "").trim();
            if (current === "" || Number(current) !== expected) {
              updatedRow.max_accounts = String(expected);
            }
          }
        } else if (type === "composite") {
          const rawGrade = row.executive_grade ?? row.executiveGrade;
          const gradeKey =
            rawGrade !== undefined && rawGrade !== null
              ? String(rawGrade).trim()
              : "";
          if (gradeKey && gradeCapacityMap[gradeKey] !== undefined) {
            const expected = gradeCapacityMap[gradeKey];
            const current = String(
              row.executive_max_accounts ?? row.executiveMaxAccounts ?? ""
            ).trim();
            if (current === "" || Number(current) !== expected) {
              updatedRow.executive_max_accounts = String(expected);
            }
          }
        }

        return updatedRow;
      });
    },
    [gradeCapacityMap]
  );

  // Whenever grades load (or change), back-fill max_accounts fields in existing preview data
  useEffect(() => {
    if (!previewData || previewData.length === 0) return;
    if (selectedType !== "executives" && selectedType !== "composite") return;

    const updated = applyCapacityLimitsToData(previewData, selectedType);

    // Only update state if something actually changed
    const hasChanged = JSON.stringify(previewData) !== JSON.stringify(updated);
    if (hasChanged) {
      setPreviewData(updated);
    }
  }, [
    grades,
    gradeCapacityMap,
    applyCapacityLimitsToData,
    previewData,
    selectedType,
  ]);

  // Filter approvers by search (name, email, role, title)
  const filteredApprovers = approverSearchQuery.trim()
    ? availableApprovers.filter(approver => {
        const q = approverSearchQuery.toLowerCase().trim();
        return (
          approver.name.toLowerCase().includes(q) ||
          (approver.email && approver.email.toLowerCase().includes(q)) ||
          (approver.role && approver.role.toLowerCase().includes(q)) ||
          (approver.title && approver.title.toLowerCase().includes(q))
        );
      })
    : availableApprovers;

  // Generate default batch name with current date/time
  const getDefaultBatchName = () => {
    const now = new Date();
    const formattedDate = now.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
    const typeLabel =
      selectedType === "executives"
        ? "Executive"
        : selectedType === "accounts"
          ? "Account"
          : selectedType === "composite"
            ? "Composite"
            : selectedType === "upload_account_team_members"
              ? "Account Team"
              : "Customer Team";
    return `${typeLabel} Upload - ${formattedDate}`;
  };

  const getUploadTypeDisplayName = (
    type: UploadType = selectedType
  ): string => {
    switch (type) {
      case "executives":
        return "Executive Sponsor";
      case "accounts":
        return "Accounts";
      case "composite":
        return "Composite";
      case "upload_account_team_members":
        return "Account Team";
      case "upload_customer_team_members":
        return "Customer Team";
      default:
        return type;
    }
  };

  const applyGeneratedDefaultBatchName = () => {
    const name = getDefaultBatchName();
    setDefaultBatchName(name);
    setBatchName(name);
    return name;
  };

  // Initialize batch name with default value
  React.useEffect(() => {
    if (!batchName) {
      applyGeneratedDefaultBatchName();
    }
  }, [selectedType]);

  // Clear upload error and related state when user changes upload type (Executives / Accounts / Composite).
  // Ensures a wrong-type error from one tab doesn't persist after switching tabs.
  React.useEffect(() => {
    setUploadError(null);
    setSelectedFile(null);
    setShowPreview(false);
    setShowSelectApprover(false);
    setShowConfirmation(false);
    setPreviewData([]);
    setEditedCells(new Set());
    setValidationErrors([]);
    setSelectedAccountIds([]);
    setAccountSearchQuery("");
  }, [selectedType]);

  const handleAccountToggle = (accountId: string) => {
    setSelectedAccountIds(prev =>
      prev.includes(accountId)
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    );
  };

  const handleSelectAllAccounts = () => {
    const visibleIds = filteredUploadAccounts.map(a => a.customer_id);
    const allVisibleSelected =
      visibleIds.length > 0 &&
      visibleIds.every(id => selectedAccountIds.includes(id));
    if (allVisibleSelected) {
      setSelectedAccountIds(prev =>
        prev.filter(id => !visibleIds.includes(id))
      );
    } else {
      setSelectedAccountIds(prev =>
        Array.from(new Set([...prev, ...visibleIds]))
      );
    }
  };

  const handleClearSelectedAccounts = () => {
    setSelectedAccountIds([]);
  };

  // Handle cell editing
  const handleCellEdit = (rowIndex: number, field: string, value: string) => {
    const updatedData = [...previewData];
    const currentRow = updatedData[rowIndex];

    // Update the field that was edited
    updatedData[rowIndex] = {
      ...currentRow,
      [field]: value,
    };

    // If grade-related field changes, auto-update corresponding max_accounts using capacity_limits
    if (selectedType === "executives" && field === "grade") {
      const gradeKey = String(value || "").trim();
      if (gradeKey && gradeCapacityMap[gradeKey] !== undefined) {
        updatedData[rowIndex].max_accounts = String(gradeCapacityMap[gradeKey]);
      }
    } else if (selectedType === "composite" && field === "executive_grade") {
      const gradeKey = String(value || "").trim();
      if (gradeKey && gradeCapacityMap[gradeKey] !== undefined) {
        updatedData[rowIndex].executive_max_accounts = String(
          gradeCapacityMap[gradeKey]
        );
      }
    }

    // Also update the alternate format if it exists (camelCase <-> snake_case)
    if (field.includes("_")) {
      // Field is snake_case, update camelCase version if it exists
      const camelCaseKey = field.replace(/_([a-z])/g, (_, letter) =>
        letter.toUpperCase()
      );
      if (currentRow[camelCaseKey] !== undefined) {
        updatedData[rowIndex][camelCaseKey] = value;
      }
    } else {
      // Field is camelCase, update snake_case version if it exists
      const snakeCaseKey = field.replace(/([A-Z])/g, "_$1").toLowerCase();
      if (currentRow[snakeCaseKey] !== undefined) {
        updatedData[rowIndex][snakeCaseKey] = value;
      }
    }

    setPreviewData(updatedData);

    // Track edited cells (track both formats if they exist)
    const cellKey = `${rowIndex}-${field}`;
    setEditedCells(prev => new Set(prev).add(cellKey));

    // Re-validate after edit
    setTimeout(() => {
      const errors = validatePreviewData(updatedData);
      setValidationErrors(errors);
    }, 0);
  };

  // Check if cell is edited
  const isCellEdited = (rowIndex: number, field: string) => {
    return editedCells.has(`${rowIndex}-${field}`);
  };

  // Start editing a cell
  const startEditing = (rowIndex: number, field: string) => {
    setEditingCell(`${rowIndex}-${field}`);
  };

  // Check if cell is being edited
  const isEditing = (rowIndex: number, field: string) => {
    return editingCell === `${rowIndex}-${field}`;
  };

  // Handle cell blur (finish editing)
  const handleCellBlur = (rowIndex: number, field: string, value: string) => {
    handleCellEdit(rowIndex, field, value);
    setEditingCell(null);
  };

  // Get all columns from data dynamically, removing duplicates (camelCase vs snake_case)
  const getColumnsFromData = (data: any[]): string[] => {
    if (data.length === 0) return [];
    // Get all unique keys from all rows
    const allKeys = new Set<string>();
    data.forEach(row => {
      Object.keys(row).forEach(key => allKeys.add(key));
    });

    // Remove duplicates where one is camelCase and other is snake_case
    const deduplicatedKeys: string[] = [];
    const processedKeys = new Set<string>();

    Array.from(allKeys).forEach(key => {
      // Skip if already processed
      if (processedKeys.has(key)) return;

      // Check if this key has a camelCase/snake_case equivalent
      let equivalentKey: string | null = null;
      if (key.includes("_")) {
        // Key is snake_case, check if camelCase version exists
        const camelCaseKey = key.replace(/_([a-z])/g, (_, letter) =>
          letter.toUpperCase()
        );
        if (allKeys.has(camelCaseKey)) {
          equivalentKey = camelCaseKey;
        }
      } else {
        // Key is camelCase, check if snake_case version exists
        const snakeCaseKey = key.replace(/([A-Z])/g, "_$1").toLowerCase();
        if (allKeys.has(snakeCaseKey)) {
          equivalentKey = snakeCaseKey;
        }
      }

      // Prefer snake_case if both exist, otherwise use the original
      if (equivalentKey) {
        const preferredKey = key.includes("_") ? key : equivalentKey;
        deduplicatedKeys.push(preferredKey);
        processedKeys.add(key);
        processedKeys.add(equivalentKey);
      } else {
        deduplicatedKeys.push(key);
        processedKeys.add(key);
      }
    });

    return deduplicatedKeys;
  };

  // Return columns in preferred display order for preview (whitelist per type).
  const getOrderedColumnsForPreview = (
    data: any[],
    type: UploadType
  ): string[] => {
    const columns = getColumnsFromData(data);
    if (columns.length === 0) return [];
    const order = PREVIEW_COLUMN_ORDER[type];
    if (!order) return columns;
    const ordered: string[] = [];
    const used = new Set<string>();
    for (const preferred of order) {
      const found = columns.find(
        c => c.toLowerCase() === preferred.toLowerCase()
      );
      if (found && !used.has(found)) {
        ordered.push(found);
        used.add(found);
      }
    }
    return ordered;
  };

  // Format column name for display (convert snake_case or camelCase to Title Case)
  const formatColumnName = (columnName: string): string => {
    // Handle snake_case
    if (columnName.includes("_")) {
      return columnName
        .split("_")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    }
    // Handle camelCase
    return columnName
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  // Get value from row for a given column (handles both camelCase and snake_case)
  const getRowValue = (row: any, column: string): string => {
    // Try direct key first
    if (row[column] !== undefined) {
      return String(row[column] || "");
    }
    // Try camelCase version if column is snake_case
    const camelCaseKey = column.replace(/_([a-z])/g, (_, letter) =>
      letter.toUpperCase()
    );
    if (row[camelCaseKey] !== undefined) {
      return String(row[camelCaseKey] || "");
    }
    // Try snake_case version if column is camelCase
    const snakeCaseKey = column.replace(/([A-Z])/g, "_$1").toLowerCase();
    if (row[snakeCaseKey] !== undefined) {
      return String(row[snakeCaseKey] || "");
    }
    return "";
  };

  // Check if a field should be read-only in the preview grid
  const isReadOnlyField = (field: string): boolean => {
    // Max accounts are prefilled from capacity_limits and should not be edited
    return field === "max_accounts" || field === "executive_max_accounts";
  };

  // Render editable cell with hover icon (unless read-only)
  const renderEditableCell = (
    rowIndex: number,
    field: string,
    value: string
  ) => {
    if (isReadOnlyField(field)) {
      return (
        <td className="min-w-[150px] border-none px-4 py-3 text-sm text-gray-700 bg-gray-50">
          <span className="inline-block w-full pr-2 text-gray-700">
            {value}
          </span>
        </td>
      );
    }
    const cellKey = `${rowIndex}-${field}`;
    const cellError = getCellValidationError(rowIndex, field);
    const hasError = !!cellError;

    return (
      <td
        className={`
          relative min-w-[150px] border-none px-4 py-3 pr-10 text-sm text-gray-700 transition-colors
          ${isCellEdited(rowIndex, field) ? "bg-violet-50 border-l-2 border-l-violet-500" : ""}
          ${isEditing(rowIndex, field) ? "bg-violet-50 p-2" : ""}
          ${hasError ? "bg-red-50 border-l-2 border-l-red-500 hover:bg-red-100" : "hover:bg-violet-50"}
          cursor-pointer
        `.trim()}
        onMouseEnter={() => setHoveredCell(cellKey)}
        onMouseLeave={() => setHoveredCell(null)}
        onClick={() => {
          if (!isEditing(rowIndex, field)) {
            startEditing(rowIndex, field);
          }
        }}
        title={cellError || "Click to edit"}
      >
        {isEditing(rowIndex, field) ? (
          <input
            type="text"
            defaultValue={value}
            autoFocus
            className={`w-full rounded border px-2 py-1.5 text-sm text-gray-800 outline-none ${hasError ? "border-red-500 bg-red-50 shadow-[0_0_0_3px_rgba(239,68,68,0.1)]" : "border-2 border-violet-500 bg-violet-50 shadow-[0_0_0_3px_rgba(139,92,246,0.1)]"}`}
            onBlur={e => handleCellBlur(rowIndex, field, e.target.value)}
            onKeyPress={e => {
              if (e.key === "Enter") {
                e.currentTarget.blur();
              }
            }}
          />
        ) : (
          <>
            <span
              className={`inline-block w-full pr-2 ${hasError ? "font-medium text-red-600" : ""}`}
            >
              {value}
            </span>
            {hasError && (
              <span
                title={cellError || undefined}
                className="absolute right-3 top-1/2 -translate-y-1/2 shrink-0"
              >
                <AlertCircle size={14} color="#ef4444" />
              </span>
            )}
            {hoveredCell === cellKey && (
              <button
                className={`absolute right-3 top-1/2 flex -translate-y-1/2 items-center justify-center rounded bg-violet-500 p-1.5 text-white shadow-sm transition-all hover:bg-violet-600 hover:shadow z-[100] ${hasError ? "right-8" : ""}`}
                onClick={() => startEditing(rowIndex, field)}
                title="Click to edit"
              >
                <Pencil size={14} />
              </button>
            )}
          </>
        )}
      </td>
    );
  };

  // Get current data for display (previewData or sample from getSampleDataForType)
  const getCurrentData = () => {
    return previewData.length > 0
      ? previewData
      : getSampleDataForType(selectedType);
  };

  const getTypeLabel = () => {
    switch (selectedType) {
      case "executives":
        return getCurrentData().length === 1
          ? "Executive Sponsor"
          : "Executive Sponsors";
      case "accounts":
        return "accounts";
      case "composite":
        return "records";
      case "upload_account_team_members":
      case "upload_customer_team_members":
        return "team members";
      default:
        return "records";
    }
  };

  // Confirmation screen title by upload type
  const getConfirmationTypeTitle = () => {
    switch (selectedType) {
      case "executives":
        return "Executive Sponsor Onboarding";
      case "accounts":
        return "Account Addition";
      case "composite":
        return "Complete Relationship Setup";
      case "upload_account_team_members":
        return "Account Team Upload";
      case "upload_customer_team_members":
        return "Customer Team Upload";
      default:
        return "Executive Sponsor Onboarding";
    }
  };

  const getConfirmationProcessing = () => {
    switch (selectedType) {
      case "accounts":
        return "Client accounts will be added to the system in unassigned state.";
      case "composite":
        return "Executive sponsors and accounts will be created with automatic assignments.";
      case "upload_account_team_members":
        return "Account team members will be linked to the selected accounts.";
      case "upload_customer_team_members":
        return "Customer team contacts will be added to the selected accounts.";
      default:
        return "Executive sponsors will be onboarded with temporary passwords for system access.";
    }
  };

  const getConfirmationOutcome = () => {
    switch (selectedType) {
      case "accounts":
        return "Accounts will be available for manual assignment to executives after approval.";
      case "composite":
        return "Executive sponsors will receive login credentials and immediate account assignments after activation.";
      case "upload_account_team_members":
        return "Account team members will be linked to the selected accounts after approval.";
      case "upload_customer_team_members":
        return "Customer team members will be added to the selected accounts after approval.";
      default:
        return "Executive sponsors will receive email invitations with login credentials after approval and activation.";
    }
  };

  const allUploadTypes = [
    {
      id: "executives" as UploadType,
      title: "Sponsors",
      description: "Upload executive sponsor team members",
      icon: "view",
      color: "#3b82f6",
    },
    {
      id: "accounts" as UploadType,
      title: "Accounts",
      description: "Upload client accounts",
      icon: "viewCalendar",
      color: "#22c55e",
    },
    {
      id: "composite" as UploadType,
      title: "Composite Data",
      description: "Upload executive sponsors, accounts & relationships",
      icon: "viewCalendar",
      color: "#8b5cf6",
    },
    {
      id: "upload_account_team_members" as UploadType,
      title: "Account Team",
      description: "Upload account team members",
      icon: "users",
      color: "#3b82f6",
    },
    {
      id: "upload_customer_team_members" as UploadType,
      title: "Customer Team",
      description: "Upload customer team members",
      icon: "userRound",
      color: "#8b5cf6",
    },
  ];

  const allowedTypeIds: UploadType[] =
    mode === "team" ? TEAM_UPLOAD_TYPES : EX_OPS_UPLOAD_TYPES;

  const uploadTypes = allUploadTypes.filter(type =>
    allowedTypeIds.includes(type.id)
  );

  const uploadTypeGridClass =
    uploadTypes.length <= 1
      ? "grid-cols-1"
      : uploadTypes.length === 2
        ? "grid-cols-1 sm:grid-cols-2"
        : uploadTypes.length === 3
          ? "grid-cols-1 sm:grid-cols-3"
          : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5";

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadError(null);
      const error = await validateCSVFile(file, selectedType);
      if (error) {
        setUploadError(error);
        setSelectedFile(null);
      } else {
        try {
          const parsedDataRaw = await parseCSVFile(file);
          const parsedData = applyCapacityLimitsToData(
            parsedDataRaw,
            selectedType
          );
          setSelectedFile(file);
          setShowPreview(true);
          setPreviewData(parsedData);
          setEditedCells(new Set()); // Reset edited cells
          // Generate default batch name
          applyGeneratedDefaultBatchName();
          // Validate the parsed data after setting it
          setTimeout(() => {
            const errors = validatePreviewData(parsedData);
            setValidationErrors(errors);
          }, 0);
        } catch (parseError) {
          setUploadError(
            parseError instanceof Error
              ? parseError.message
              : "Failed to parse CSV file"
          );
          setSelectedFile(null);
        }
      }
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.type === "text/csv") {
      setUploadError(null);
      const error = await validateCSVFile(file, selectedType);
      if (error) {
        setUploadError(error);
        setSelectedFile(null);
      } else {
        try {
          const parsedDataRaw = await parseCSVFile(file);
          const parsedData = applyCapacityLimitsToData(
            parsedDataRaw,
            selectedType
          );
          setSelectedFile(file);
          setShowPreview(true);
          setPreviewData(parsedData);
          setEditedCells(new Set()); // Reset edited cells
          // Generate default batch name
          applyGeneratedDefaultBatchName();
          // Validate the parsed data after setting it
          setTimeout(() => {
            const errors = validatePreviewData(parsedData);
            setValidationErrors(errors);
          }, 0);
        } catch (parseError) {
          setUploadError(
            parseError instanceof Error
              ? parseError.message
              : "Failed to parse CSV file"
          );
          setSelectedFile(null);
        }
      }
    }
  };

  const getRequiredColumns = (type: UploadType) => {
    switch (type) {
      case "executives":
        return ["name", "title", "email", "phone", "city", "country"];
      case "accounts":
        return [
          "name",
          "industry",
          "tier",
          "city",
          "country",
          "contact_person",
          "contact_email",
        ];
      case "composite":
        return [
          // Executive required fields
          "executive_name",
          "executive_title",
          "executive_email",
          "executive_region",
          "executive_grade",
          // Account required fields
          "account_name",
          "account_industry",
          "account_tier",
          "account_city",
          "account_country",
          "account_contact_person",
          "account_contact_email",
        ];
      case "upload_account_team_members":
      case "upload_customer_team_members":
        return [...getTeamRequiredColumns()];
      default:
        return [];
    }
  };

  const validateSelectedAccountId = (
    row: any,
    rowIndex: number
  ): Array<{ rowIndex: number; field: string; message: string }> => {
    const errors: Array<{ rowIndex: number; field: string; message: string }> =
      [];
    const accountId = String(row.account_id ?? "").trim();
    const accountIdError = validateRequiredString(row.account_id, "account_id");
    if (accountIdError) {
      errors.push({ rowIndex, field: "account_id", message: accountIdError });
    } else if (
      selectedAccountIds.length > 0 &&
      !selectedAccountIds.includes(accountId)
    ) {
      errors.push({
        rowIndex,
        field: "account_id",
        message:
          "account_id must match one of the accounts selected for this upload",
      });
    }
    const accountNameError = validateRequiredString(
      row.account_name,
      "account_name"
    );
    if (accountNameError) {
      errors.push({
        rowIndex,
        field: "account_name",
        message: accountNameError,
      });
    }
    return errors;
  };

  // Validation functions for Executive template
  const validateEmail = (email: string): string | null => {
    if (!email || typeof email !== "string") {
      return "email is required";
    }
    const trimmedEmail = email.trim();
    if (trimmedEmail === "") {
      return "email is required";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      return "Invalid email format";
    }
    return null;
  };

  const validateRequiredString = (
    value: any,
    fieldName: string
  ): string | null => {
    if (value === null || value === undefined) {
      return `${fieldName} is required`;
    }
    const stringValue = String(value).trim();
    if (stringValue === "") {
      return `${fieldName} is required`;
    }
    return null;
  };

  const validateRegion = (region: string): string | null => {
    const error = validateRequiredString(region, "region");
    if (error) {
      return "region is required and cannot be empty";
    }
    const validRegions = regionOptions.map(r => r.value);
    const trimmedRegion = String(region).trim();
    if (!validRegions.includes(trimmedRegion)) {
      return `region must be one of: ${validRegions.join(", ")} (got: "${trimmedRegion}")`;
    }
    return null;
  };

  const validateGrade = (grade: string | number): string | null => {
    if (grade === null || grade === undefined || grade === "") {
      return "grade is required";
    }

    const gradeStr = String(grade).trim();

    // If grades are loaded from API, validate dynamically against them
    if (grades?.items && Array.isArray(grades.items)) {
      const validGrades = grades.items
        .map((item: any) => item.grade)
        .filter((g: any) => g !== null && g !== undefined)
        .map((g: any) => String(g).trim());

      if (validGrades.length > 0) {
        if (!validGrades.includes(gradeStr)) {
          return `grade must be one of: ${validGrades.join(", ")}`;
        }
        return null;
      }
    }

    // Fallback: basic numeric check (for safety if API not available)
    if (!/^\d+$/.test(gradeStr)) {
      return "grade must be a valid number";
    }

    return null;
  };

  const validateRole = (role: string): string | null => {
    const error = validateRequiredString(role, "role");
    if (error) {
      return error;
    }
    const normalizedRole = normalizeRoleName(role);
    if (!normalizedRole) {
      return "role is required";
    }
    // Only these Data Hub labels are valid in upload files.
    if (
      DATA_HUB_ROLE_LABELS.includes(
        normalizedRole as (typeof DATA_HUB_ROLE_LABELS)[number]
      )
    ) {
      return null;
    }

    return `role must be one of: ${DATA_HUB_ROLE_LABELS.join(", ")} (got: "${role}")`;
  };

  // Validation functions for Account template
  const validateTier = (tier: string): string | null => {
    if (tier === null || tier === undefined || tier === "") {
      return "tier is required";
    }
    const tierStr = String(tier).trim().toUpperCase();
    const validTiers = ["T1", "T2", "T3"];
    if (!validTiers.includes(tierStr)) {
      return "Tier must be: T1, T2, or T3";
    }
    return null;
  };

  const validateRevenue = (revenue: string | number): string | null => {
    // Optional field - if empty or null, it's valid
    if (revenue === null || revenue === undefined || revenue === "") {
      return null;
    }

    const revenueStr = String(revenue).trim();

    // Check if it's a valid number
    if (isNaN(Number(revenueStr))) {
      return `Revenue must be a valid number with maximum 2 decimal places (got: "${revenueStr}")`;
    }

    // Check for commas or currency symbols (not allowed)
    if (/[,$]/.test(revenueStr)) {
      return `Revenue must be a valid number with maximum 2 decimal places (got: "${revenueStr}")`;
    }

    // Check decimal places
    const decimalParts = revenueStr.split(".");
    if (decimalParts.length > 2) {
      return `Revenue must be a valid number with maximum 2 decimal places (got: "${revenueStr}")`;
    }
    if (decimalParts.length === 2 && decimalParts[1].length > 2) {
      return `Revenue must be a valid number with maximum 2 decimal places (got: "${revenueStr}")`;
    }

    const revenueNum = Number(revenueStr);

    // Check if negative
    if (revenueNum < 0) {
      return "Revenue cannot be negative";
    }

    // Check if exceeds MAX_SAFE_INTEGER
    if (revenueNum > Number.MAX_SAFE_INTEGER) {
      return `Revenue exceeds maximum safe value (${Number.MAX_SAFE_INTEGER})`;
    }

    return null;
  };

  const validateEmployees = (employees: string | number): string | null => {
    // Optional field - if empty or null, it's valid
    if (employees === null || employees === undefined || employees === "") {
      return null;
    }

    const employeesStr = String(employees).trim();

    // Check if it's a valid number
    if (isNaN(Number(employeesStr))) {
      return `employees must be a valid number (got: "${employeesStr}")`;
    }

    // Check for commas (not allowed in number format)
    if (/,/.test(employeesStr)) {
      return `employees must be a valid number (got: "${employeesStr}")`;
    }

    return null;
  };

  const validateRenewalDate = (date: string): string | null => {
    // Optional field - if empty or null, it's valid
    if (date === null || date === undefined || date === "") {
      return null;
    }

    const dateStr = String(date).trim();
    const parsedDate = new Date(dateStr);

    // Check if date is valid
    if (isNaN(parsedDate.getTime())) {
      return `renewal_date must be a valid date (got: "${dateStr}"). Use format: YYYY-MM-DD`;
    }

    return null;
  };

  const validateChurnRisk = (churnRisk: string): string | null => {
    // Optional field - if empty or null, it's valid
    if (churnRisk === null || churnRisk === undefined || churnRisk === "") {
      return null;
    }

    const churnRiskStr = String(churnRisk).trim().toLowerCase();
    const validChurnRisks = ["low", "medium", "high"];

    if (!validChurnRisks.includes(churnRiskStr)) {
      return `churn_risk must be: low, medium, or high (got: "${churnRisk}")`;
    }

    return null;
  };

  // Validate a single row for Executive template
  const validateExecutiveRow = (
    row: any,
    rowIndex: number
  ): Array<{ rowIndex: number; field: string; message: string }> => {
    const errors: Array<{ rowIndex: number; field: string; message: string }> =
      [];

    // Validate required fields
    const nameError = validateRequiredString(row.name, "name");
    if (nameError) {
      errors.push({ rowIndex, field: "name", message: nameError });
    }

    const titleError = validateRequiredString(row.title, "title");
    if (titleError) {
      errors.push({ rowIndex, field: "title", message: titleError });
    }

    const emailError = validateEmail(row.email);
    if (emailError) {
      errors.push({ rowIndex, field: "email", message: emailError });
    }

    const regionError = validateRegion(row.region);
    if (regionError) {
      errors.push({ rowIndex, field: "region", message: regionError });
    }

    const gradeError = validateGrade(row.grade);
    if (gradeError) {
      errors.push({ rowIndex, field: "grade", message: gradeError });
    }

    const roleError = validateRole(row.role);
    if (roleError) {
      errors.push({ rowIndex, field: "role", message: roleError });
    }

    return errors;
  };

  // Validate a single row for Account template
  const validateAccountRow = (
    row: any,
    rowIndex: number
  ): Array<{ rowIndex: number; field: string; message: string }> => {
    const errors: Array<{ rowIndex: number; field: string; message: string }> =
      [];

    // Validate required fields
    const nameError = validateRequiredString(row.name, "name");
    if (nameError) {
      errors.push({ rowIndex, field: "name", message: nameError });
    }

    const industryError = validateRequiredString(row.industry, "industry");
    if (industryError) {
      errors.push({ rowIndex, field: "industry", message: industryError });
    }

    const tierError = validateTier(row.tier);
    if (tierError) {
      errors.push({ rowIndex, field: "tier", message: tierError });
    }

    const cityError = validateRequiredString(row.city, "city");
    if (cityError) {
      errors.push({ rowIndex, field: "city", message: cityError });
    }

    const countryError = validateRequiredString(row.country, "country");
    if (countryError) {
      errors.push({ rowIndex, field: "country", message: countryError });
    }

    const contactPersonError = validateRequiredString(
      row.contact_person,
      "contact_person"
    );
    if (contactPersonError) {
      errors.push({
        rowIndex,
        field: "contact_person",
        message: contactPersonError,
      });
    }

    // Validate contact_email (required with email format)
    const contactEmailError = validateEmail(row.contact_email);
    if (contactEmailError) {
      errors.push({
        rowIndex,
        field: "contact_email",
        message: contactEmailError.replace("email", "contact email"),
      });
    }

    // Validate optional fields
    const revenueError = validateRevenue(row.revenue);
    if (revenueError) {
      errors.push({ rowIndex, field: "revenue", message: revenueError });
    }

    const employeesError = validateEmployees(row.employees);
    if (employeesError) {
      errors.push({ rowIndex, field: "employees", message: employeesError });
    }

    const renewalDateError = validateRenewalDate(row.renewal_date);
    if (renewalDateError) {
      errors.push({
        rowIndex,
        field: "renewal_date",
        message: renewalDateError,
      });
    }

    const churnRiskError = validateChurnRisk(row.churn_risk);
    if (churnRiskError) {
      errors.push({ rowIndex, field: "churn_risk", message: churnRiskError });
    }

    return errors;
  };

  // Validate a single row for Composite template
  const validateCompositeRow = (
    row: any,
    rowIndex: number
  ): Array<{ rowIndex: number; field: string; message: string }> => {
    const errors: Array<{ rowIndex: number; field: string; message: string }> =
      [];

    // Validate Executive required fields
    const executiveNameError = validateRequiredString(
      row.executive_name,
      "executive_name"
    );
    if (executiveNameError) {
      errors.push({
        rowIndex,
        field: "executive_name",
        message: executiveNameError.replace("executive_name", "executive name"),
      });
    }

    const executiveTitleError = validateRequiredString(
      row.executive_title,
      "executive_title"
    );
    if (executiveTitleError) {
      errors.push({
        rowIndex,
        field: "executive_title",
        message: executiveTitleError.replace(
          "executive_title",
          "executive title"
        ),
      });
    }

    const executiveEmailError = validateEmail(row.executive_email);
    if (executiveEmailError) {
      errors.push({
        rowIndex,
        field: "executive_email",
        message: executiveEmailError.replace("email", "executive email"),
      });
    }

    const executiveRegionError = validateRegion(row.executive_region);
    if (executiveRegionError) {
      errors.push({
        rowIndex,
        field: "executive_region",
        message: executiveRegionError.replace("region", "executive region"),
      });
    }

    const executiveGradeError = validateGrade(row.executive_grade);
    if (executiveGradeError) {
      errors.push({
        rowIndex,
        field: "executive_grade",
        message: executiveGradeError.replace("grade", "executive grade"),
      });
    }

    // Validate Account required fields
    const accountNameError = validateRequiredString(
      row.account_name,
      "account_name"
    );
    if (accountNameError) {
      errors.push({
        rowIndex,
        field: "account_name",
        message: accountNameError.replace("account_name", "account name"),
      });
    }

    const accountIndustryError = validateRequiredString(
      row.account_industry,
      "account_industry"
    );
    if (accountIndustryError) {
      errors.push({
        rowIndex,
        field: "account_industry",
        message: accountIndustryError.replace(
          "account_industry",
          "account industry"
        ),
      });
    }

    const accountTierError = validateTier(row.account_tier);
    if (accountTierError) {
      errors.push({
        rowIndex,
        field: "account_tier",
        message: accountTierError.replace("Tier", "Account tier"),
      });
    }

    const accountCityError = validateRequiredString(
      row.account_city,
      "account_city"
    );
    if (accountCityError) {
      errors.push({
        rowIndex,
        field: "account_city",
        message: accountCityError.replace("account_city", "account city"),
      });
    }

    const accountCountryError = validateRequiredString(
      row.account_country,
      "account_country"
    );
    if (accountCountryError) {
      errors.push({
        rowIndex,
        field: "account_country",
        message: accountCountryError.replace(
          "account_country",
          "account country"
        ),
      });
    }

    const accountContactPersonError = validateRequiredString(
      row.account_contact_person,
      "account_contact_person"
    );
    if (accountContactPersonError) {
      errors.push({
        rowIndex,
        field: "account_contact_person",
        message: accountContactPersonError.replace(
          "account_contact_person",
          "account contact person"
        ),
      });
    }

    const accountContactEmailError = validateEmail(row.account_contact_email);
    if (accountContactEmailError) {
      errors.push({
        rowIndex,
        field: "account_contact_email",
        message: accountContactEmailError.replace(
          "email",
          "account contact email"
        ),
      });
    }

    // Validate Account optional fields
    const accountRevenueError = validateRevenue(row.account_revenue);
    if (accountRevenueError) {
      errors.push({
        rowIndex,
        field: "account_revenue",
        message: accountRevenueError.replace("Revenue", "Account revenue"),
      });
    }

    const accountEmployeesError = validateEmployees(row.account_employees);
    if (accountEmployeesError) {
      errors.push({
        rowIndex,
        field: "account_employees",
        message: accountEmployeesError.replace(
          "employees",
          "account employees"
        ),
      });
    }

    const accountRenewalDateError = validateRenewalDate(
      row.account_renewal_date
    );
    if (accountRenewalDateError) {
      errors.push({
        rowIndex,
        field: "account_renewal_date",
        message: accountRenewalDateError.replace(
          "renewal_date",
          "account renewal date"
        ),
      });
    }

    return errors;
  };

  const getTeamRowAccountKey = (row: any): string => {
    const accountId = String(row.account_id || row.accountId || "").trim();
    if (accountId) return `id:${accountId}`;
    const accountName = String(row.account_name || row.accountName || "")
      .trim()
      .toLowerCase();
    if (accountName) return `name:${accountName}`;
    return "";
  };

  const normalizeTeamMemberEmail = (row: any): string =>
    String(row.member_email || row.email || "")
      .trim()
      .toLowerCase();

  const validateDuplicateTeamEmailsPerAccount = (
    data: any[]
  ): Array<{ rowIndex: number; field: string; message: string }> => {
    const rowsByAccountEmail = new Map<string, number[]>();

    data.forEach((row, index) => {
      const accountKey = getTeamRowAccountKey(row);
      const email = normalizeTeamMemberEmail(row);
      if (!accountKey || !email) return;

      const compositeKey = `${accountKey}|${email}`;
      const rows = rowsByAccountEmail.get(compositeKey) ?? [];
      rows.push(index);
      rowsByAccountEmail.set(compositeKey, rows);
    });

    const errors: Array<{ rowIndex: number; field: string; message: string }> =
      [];

    rowsByAccountEmail.forEach(rows => {
      if (rows.length < 2) return;

      const rowLabels = rows.map(i => i + 1).join(", ");
      rows.forEach(rowIndex => {
        errors.push({
          rowIndex,
          field: "member_email",
          message: `Duplicate member email for this account (rows ${rowLabels})`,
        });
      });
    });

    return errors;
  };

  const validateTeamMemberEmail = (
    row: any,
    rowIndex: number
  ): Array<{ rowIndex: number; field: string; message: string }> => {
    const errors: Array<{ rowIndex: number; field: string; message: string }> =
      [];
    const email = row.member_email;
    if (!email || String(email).trim() === "") {
      errors.push({
        rowIndex,
        field: "member_email",
        message: "member_email is required",
      });
    } else {
      const emailError = validateEmail(email);
      if (emailError) {
        errors.push({
          rowIndex,
          field: "member_email",
          message: emailError.replace("email", "member email"),
        });
      }
    }
    return errors;
  };

  const validateAccountTeamRow = (
    row: any,
    rowIndex: number
  ): Array<{ rowIndex: number; field: string; message: string }> => {
    const errors = [
      ...validateSelectedAccountId(row, rowIndex),
      ...validateTeamMemberEmail(row, rowIndex),
    ];

    const memberNameError = validateRequiredString(
      row.member_name,
      "member_name"
    );
    if (memberNameError) {
      errors.push({
        rowIndex,
        field: "member_name",
        message: memberNameError.replace("member_name", "member name"),
      });
    }

    const memberTitleError = validateRequiredString(
      row.member_title,
      "member_title"
    );
    if (memberTitleError) {
      errors.push({
        rowIndex,
        field: "member_title",
        message: memberTitleError.replace("member_title", "member title"),
      });
    }

    const roleError = validateRequiredString(row.role, "role");
    if (roleError) {
      errors.push({ rowIndex, field: "role", message: roleError });
    } else {
      const roleVal = String(row.role || "")
        .trim()
        .toUpperCase();
      if (
        !ACCOUNT_TEAM_ALLOWED_ROLES.includes(
          roleVal as (typeof ACCOUNT_TEAM_ALLOWED_ROLES)[number]
        )
      ) {
        errors.push({
          rowIndex,
          field: "role",
          message: `Role must be one of: ${ACCOUNT_TEAM_ALLOWED_ROLES.join(", ")} (got: "${row.role}")`,
        });
      }
    }

    return errors;
  };

  const normalizeTeamUploadRows = (data: any[]): any[] => {
    if (selectedType !== "upload_customer_team_members") return data;
    return data.map(row => {
      const normalizedRole = normalizeCustomerTeamRole(row.role);
      return normalizedRole ? { ...row, role: normalizedRole } : row;
    });
  };

  const validateCustomerTeamRow = (
    row: any,
    rowIndex: number
  ): Array<{ rowIndex: number; field: string; message: string }> => {
    const errors = [
      ...validateSelectedAccountId(row, rowIndex),
      ...validateTeamMemberEmail(row, rowIndex),
    ];

    const memberNameError = validateRequiredString(
      row.member_name,
      "member_name"
    );
    if (memberNameError) {
      errors.push({
        rowIndex,
        field: "member_name",
        message: memberNameError.replace("member_name", "member name"),
      });
    }

    const memberTitleError = validateRequiredString(
      row.member_title,
      "member_title"
    );
    if (memberTitleError) {
      errors.push({
        rowIndex,
        field: "member_title",
        message: memberTitleError.replace("member_title", "member title"),
      });
    }

    const roleError = validateRequiredString(row.role, "role");
    if (roleError) {
      errors.push({ rowIndex, field: "role", message: roleError });
    } else if (!normalizeCustomerTeamRole(row.role)) {
      errors.push({
        rowIndex,
        field: "role",
        message: `${CUSTOMER_TEAM_ROLE_VALIDATION_MESSAGE} (got: "${row.role}")`,
      });
    }

    return errors;
  };

  /** Validate team upload rows against organization account list (reference parity) */
  const validateTeamRowsAgainstAccounts = (
    data: any[]
  ): Array<{ rowIndex: number; field: string; message: string }> => {
    const errors: Array<{ rowIndex: number; field: string; message: string }> =
      [];
    const allowedIds = new Set(
      uploadAccounts.map(a => String(a.customer_id).trim())
    );
    const accountsById = new Map(
      uploadAccounts.map(a => [String(a.customer_id).trim(), a])
    );
    const allowedNames = new Set(
      uploadAccounts.map(a => (a.name || "").toString().trim().toLowerCase())
    );

    data.forEach((row, index) => {
      const rowId = String(row.account_id || row.accountId || "").trim();
      const rowName = String(row.account_name || row.accountName || "")
        .trim()
        .toLowerCase();

      if (rowId && rowName) {
        if (!allowedIds.has(rowId)) {
          errors.push({
            rowIndex: index,
            field: "account_id",
            message: `account_id "${rowId}" not found in organization`,
          });
          return;
        }
        const matched = accountsById.get(rowId);
        const expectedName = (matched?.name || "")
          .toString()
          .trim()
          .toLowerCase();
        if (expectedName && expectedName !== rowName) {
          errors.push({
            rowIndex: index,
            field: "account_name",
            message: `account_name does not match account_id (expected "${matched?.name}")`,
          });
        }
        return;
      }

      if (rowId) {
        if (!allowedIds.has(rowId)) {
          errors.push({
            rowIndex: index,
            field: "account_id",
            message: `account_id "${rowId}" not found in organization`,
          });
        }
        return;
      }

      if (rowName) {
        if (!allowedNames.has(rowName)) {
          errors.push({
            rowIndex: index,
            field: "account_name",
            message: `account_name "${row.account_name}" not found in organization`,
          });
        }
        return;
      }

      errors.push({
        rowIndex: index,
        field: "account_id",
        message: "Missing account_name or account_id",
      });
    });

    return errors;
  };

  // Validate all rows in the preview data
  const validatePreviewData = (
    data?: any[]
  ): Array<{
    rowIndex: number;
    field: string;
    message: string;
  }> => {
    if (
      selectedType !== "executives" &&
      selectedType !== "accounts" &&
      selectedType !== "composite" &&
      !isTeamUploadType(selectedType)
    ) {
      return [];
    }

    const allErrors: Array<{
      rowIndex: number;
      field: string;
      message: string;
    }> = [];

    // Use provided data or current previewData
    const dataToValidate = normalizeTeamUploadRows(data || previewData);

    if (dataToValidate.length === 0) {
      return [];
    }

    dataToValidate.forEach((row, index) => {
      if (selectedType === "executives") {
        const rowErrors = validateExecutiveRow(row, index);
        allErrors.push(...rowErrors);
      } else if (selectedType === "accounts") {
        const rowErrors = validateAccountRow(row, index);
        allErrors.push(...rowErrors);
      } else if (selectedType === "composite") {
        const rowErrors = validateCompositeRow(row, index);
        allErrors.push(...rowErrors);
      } else if (selectedType === "upload_account_team_members") {
        const rowErrors = validateAccountTeamRow(row, index);
        allErrors.push(...rowErrors);
      } else if (selectedType === "upload_customer_team_members") {
        const rowErrors = validateCustomerTeamRow(row, index);
        allErrors.push(...rowErrors);
      }
    });

    if (isTeamUploadType(selectedType)) {
      allErrors.push(...validateTeamRowsAgainstAccounts(dataToValidate));
    }

    if (
      selectedType === "upload_account_team_members" ||
      selectedType === "upload_customer_team_members"
    ) {
      allErrors.push(...validateDuplicateTeamEmailsPerAccount(dataToValidate));
    }

    return allErrors;
  };

  // Get validation error for a specific cell
  const getCellValidationError = (
    rowIndex: number,
    field: string
  ): string | null => {
    const error = validationErrors.find(
      err => err.rowIndex === rowIndex && err.field === field
    );
    return error ? error.message : null;
  };

  /* eslint-disable */
  // Parse CSV file and return data
  const parseCSVFile = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => {
        try {
          const text = e.target?.result as string;
          const lines = text.split("\n").filter(line => line.trim() !== "");

          if (lines.length < 2) {
            reject(
              new Error(
                "File must contain at least a header row and one data row"
              )
            );
            return;
          }

          // Parse headers (lowercase for consistent row keys, matching reference)
          const headers = lines[0]
            .split(",")
            .map(h => h.trim().replace(/"/g, "").toLowerCase());

          // Parse data rows
          const data = lines.slice(1).map(line => {
            // Simple CSV parsing - handles quoted values
            const values: string[] = [];
            let currentValue = "";
            let inQuotes = false;

            for (let i = 0; i < line.length; i++) {
              const char = line[i];
              const nextChar = line[i + 1];

              if (char === '"') {
                if (inQuotes && nextChar === '"') {
                  // Escaped quote
                  currentValue += '"';
                  i++; // Skip next quote
                } else {
                  // Toggle quote state
                  inQuotes = !inQuotes;
                }
              } else if (char === "," && !inQuotes) {
                // End of field
                values.push(currentValue.trim());
                currentValue = "";
              } else {
                currentValue += char;
              }
            }
            // Push last value
            values.push(currentValue.trim());

            // Create object from headers and values
            const row: any = {};
            headers.forEach((header, index) => {
              row[header] = values[index] || "";
            });
            return row;
          });

          resolve(data);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsText(file);
    });
  };
  /* eslint-enable */

  const validateCSVFile = (
    file: File,
    type: UploadType
  ): Promise<string | null> => {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = e => {
        const text = e.target?.result as string;
        const lines = text.split("\n").filter(line => line.trim() !== "");
        if (lines.length < 2) {
          resolve("File must contain at least a header row and one data row");
          return;
        }

        const headers = lines[0]
          .split(",")
          .map(h => h.trim().replace(/"/g, "").toLowerCase());
        const requiredColumns = getRequiredColumns(type);
        const missingColumns = requiredColumns.filter(
          col => !headers.includes(col.toLowerCase())
        );

        if (missingColumns.length > 0) {
          resolve(`Missing required columns: ${missingColumns.join(", ")}`);
        } else {
          resolve(null);
        }
      };
      reader.readAsText(file);
    });
  };

  const handleDownloadTemplate = () => {
    setUploadError(null);
    if (
      isTeamUploadType(selectedType) &&
      selectedAccountsForUpload.length === 0
    ) {
      setUploadError(
        "Select at least one account before downloading the template."
      );
      return;
    }
    const sampleData = getSampleDataForType(
      selectedType,
      isTeamUploadType(selectedType) ? selectedAccountsForUpload : undefined
    );

    // Get all column names from the first sample data object
    // This ensures we include all fields in the template, not just required ones
    const allColumns = isTeamUploadType(selectedType)
      ? [...getTeamTemplateColumns()]
      : sampleData.length > 0
        ? Object.keys(sampleData[0])
        : getRequiredColumns(selectedType);

    const csvContent = [
      allColumns.join(","),
      ...sampleData.map(row =>
        allColumns
          .map(header => {
            const value = row[header as keyof typeof row];
            // Handle undefined/null values and escape quotes
            const stringValue =
              value !== undefined && value !== null ? String(value) : "";
            // Escape quotes in the value
            return `"${stringValue.replace(/"/g, "'\"\"'")}"`;
          })
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const fileSuffix =
      isTeamUploadType(selectedType) && selectedAccountsForUpload.length > 0
        ? `_${selectedAccountsForUpload.length}_accounts`
        : "";
    link.download = `${selectedType}_template${fileSuffix}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const getSampleDataForType = (
    type: UploadType,
    selectedAccounts?: Array<{ customer_id: string; name: string }>
  ) => {
    const timestamp = Date.now();
    const randomSuffix = Math.random().toString(36).substr(2, 5);

    if (isTeamUploadType(type) && selectedAccounts?.length) {
      return selectedAccounts.map((account, index) =>
        buildTeamMemberSampleRow(account, type as TeamUploadType, {
          includeSampleMember: index === 0,
          randomSuffix,
        })
      );
    }

    switch (type) {
      case "executives":
        return [
          {
            name: "David Rodriguez",
            title: "Executive Director",
            email: "david.rodriguez@izba.ai",
            phone: "+1-214-555-0300",
            city: "Dallas",
            country: "United States",
            region: "Americas",
            specialization: "Technology Solutions",
            grade: 1,
            max_accounts: "12",
            certifications: "Azure Architect, CISSP",
            languages: "English, Spanish",
            role: "sponsor",
          },
          {
            name: "Lisa Andersson",
            title: "Senior Account Executive",
            email: "lisa.andersson@izba.ai",
            phone: "+46-8-555-0400",
            city: "Stockholm",
            country: "Sweden",
            region: "EMEA",
            specialization: "Manufacturing & Industrial",
            grade: 2,
            max_accounts: "8",
            certifications: "Lean Six Sigma, PMP",
            languages: "Swedish, English, German",
            role: "sponsor",
          },
        ];
      case "accounts":
        return [
          {
            name: "TechFlow Industries",
            industry: "Software",
            tier: "T1",
            city: "Austin",
            country: "United States",
            contact_person: "Michael Chen",
            contact_email: "michael.chen@techflow.com",
            contact_phone: "+1-512-555-0100",
            revenue: "75000000",
            employees: "850",
            churn_risk: "low",
            renewal_date: "2025-09-15",
          },
          {
            name: "Nordic Solutions AB",
            industry: "Manufacturing",
            tier: "T2",
            city: "Stockholm",
            country: "Sweden",
            contact_person: "Emma Larsson",
            contact_email: "emma.larsson@nordic.se",
            contact_phone: "+46-8-555-0200",
            revenue: "45000000",
            employees: "320",
            churn_risk: "medium",
            renewal_date: "2026-03-20",
          },
        ];
      case "composite":
        return [
          {
            executive_name: "John Smith",
            executive_title: "Senior Executive Sponsor",
            executive_email: `john.smith.${timestamp}.${randomSuffix}@izba.ai`,
            executive_phone: "+1-555-0123",
            executive_region: "Americas",
            executive_specialization: "Enterprise Solutions",
            executive_grade: 1,
            executive_max_accounts: "12",
            executive_certifications: "PMP, Salesforce Admin",
            executive_languages: "English, Spanish",
            account_name: `TechCorp Solutions ${randomSuffix}`,
            account_industry: "Technology",
            account_tier: "T1",
            account_city: "San Francisco",
            account_country: "USA",
            account_contact_person: "Jane Doe",
            account_contact_email: `jane.doe.${timestamp}.${randomSuffix}@techcorp.com`,
            account_contact_phone: "+1-555-0456",
            account_revenue: "50000000",
            account_employees: "500",
            account_renewal_date: "2025-12-31",
            relationship_type: "primary",
          },
          {
            executive_name: "Sarah Johnson",
            executive_title: "Executive Sponsor",
            executive_email: `sarah.johnson.${timestamp}.${randomSuffix}@izba.ai`,
            executive_phone: "+1-555-0789",
            executive_region: "EMEA",
            executive_specialization: "Financial Services",
            executive_grade: 2,
            executive_max_accounts: "8",
            executive_certifications: "CISSP, ITIL",
            executive_languages: "English, French",
            account_name: `GlobalBank Corp ${randomSuffix}`,
            account_industry: "Financial Services",
            account_tier: "T2",
            account_city: "London",
            account_country: "UK",
            account_contact_person: "Robert Wilson",
            account_contact_email: `robert.wilson.${timestamp}.${randomSuffix}@globalbank.com`,
            account_contact_phone: "+44-20-7123-4567",
            account_revenue: "75000000",
            account_employees: "1200",
            account_renewal_date: "2026-06-30",
            relationship_type: "secondary",
          },
        ];
      case "upload_account_team_members":
        return ACCOUNT_TEAM_STATIC_SAMPLES;
      case "upload_customer_team_members":
        return CUSTOMER_TEAM_STATIC_SAMPLES;
      default:
        return [];
    }
  };

  const handleBackToUpload = () => {
    setShowPreview(false);
    setShowSelectApprover(false);
    setShowConfirmation(false);
    setSelectedFile(null);
    setDefaultBatchName("");
    setBatchName("");
    setSelectedApprover(null);
  };

  const handleBackToPreview = () => {
    setShowSelectApprover(false);
  };

  const handleBackToApprover = () => {
    setShowConfirmation(false);
  };

  const handleContinueToConfirm = () => {
    setShowConfirmation(true);
  };

  // Convert data array to CSV file
  const createCSVFileFromData = (data: any[]): File => {
    if (data.length === 0) {
      // If no data, return empty CSV
      const csvContent = "";
      const blob = new Blob([csvContent], { type: "text/csv" });
      return new File([blob], "upload.csv", { type: "text/csv" });
    }

    // Get all columns from the data
    const allColumns = getColumnsFromData(data);

    // Create CSV content
    const csvRows: string[] = [];

    // Add header row
    csvRows.push(allColumns.join(","));

    // Add data rows
    data.forEach(row => {
      const values = allColumns.map(column => {
        // Get value from row (handles both camelCase and snake_case)
        const value = getRowValue(row, column);
        // Always wrap in quotes and escape quotes for CSV safety
        const escapedValue = String(value || "").replace(/"/g, "'\"\"'");
        return `"${escapedValue}"`;
      });
      csvRows.push(values.join(","));
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });

    // Use original filename if available, otherwise generate one
    const fileName =
      selectedFile?.name || `${selectedType}_upload_${Date.now()}.csv`;
    return new File([blob], fileName, { type: "text/csv" });
  };

  const handleSubmitForReview = async () => {
    // Validate required fields
    const currentData = getCurrentData();
    if (currentData.length === 0) {
      setUploadError("No data available to upload");
      return;
    }

    if (!selectedApprover) {
      setUploadError("Please select an approver");
      return;
    }

    let resolvedBatchName = batchName.trim();
    let baseline = defaultBatchName.trim();
    if (!resolvedBatchName) {
      resolvedBatchName = applyGeneratedDefaultBatchName();
      baseline = resolvedBatchName;
    }

    // Re-validate data before submission
    const dataToSubmit = normalizeTeamUploadRows(
      previewData.length > 0 ? previewData : currentData
    );
    const errors = validatePreviewData(dataToSubmit);
    setValidationErrors(errors);

    if (errors.length > 0) {
      setUploadError(
        `Please fix ${errors.length} validation error(s) before submitting. See the preview table for details.`
      );
      return;
    }

    // Create CSV file from current data (includes any edits made)
    const csvFile = createCSVFileFromData(dataToSubmit);

    // Dispatch bulk upload action with the CSV file
    const batchNameForBackend =
      resolvedBatchName === baseline ? null : resolvedBatchName;

    dispatch(
      bulkUpload({
        file: csvFile,
        type: selectedType,
        approverId: selectedApprover,
        batchName: batchNameForBackend,
      })
    );
  };

  const buildAccountTeamCsvValidationErrors = (
    result: AccountTeamCsvValidateResult | null | undefined
  ): string[] => {
    if (!result) return ["Unable to validate account team CSV"];

    const errors: string[] = [];

    (result.parse_errors ?? []).forEach(message => {
      errors.push(message);
    });

    (result.validation_errors ?? []).forEach(item => {
      const field = item.field ? ` (${item.field})` : "";
      errors.push(`Row ${item.row}${field}: ${item.message}`);
    });

    (result.invalid_roles ?? []).forEach(item => {
      errors.push(`Row ${item.row}: ${item.message}`);
    });

    (result.duplicate_memberships ?? []).forEach(item => {
      errors.push(`Row ${item.row}: ${item.message}`);
    });

    (result.missing_name_for_new_users ?? []).forEach(item => {
      errors.push(`Row ${item.row}: ${item.message}`);
    });

    (result.duplicate_rows ?? []).forEach(item => {
      errors.push(
        `Duplicate account team rows for ${item.email} (rows ${item.rows.join(", ")})`
      );
    });

    (result.intra_batch_role_conflicts ?? []).forEach(item => {
      errors.push(
        `Row ${item.rows.join(", ")}: ${item.email} has conflicting roles in CSV (${item.roles.join(", ")})`
      );
    });

    (result.role_mismatches ?? []).forEach(item => {
      errors.push(
        `Row ${item.row}: ${item.email} role mismatch — incoming role ${item.incoming_role ?? "—"} does not match organization role ${item.current_role}`
      );
    });

    const byAccountId = result.role_limits?.by_account_id ?? {};
    Object.entries(byAccountId).forEach(([accountId, accountStats]) => {
      const accountLabel = accountStats.account_name?.trim() || accountId;
      (accountStats.violations ?? []).forEach(violation => {
        errors.push(
          `Role limit exceeded for account "${accountLabel}": ${violation.role_key} limit=${violation.limit}, existing=${violation.existing}, incoming=${violation.incoming}`
        );
      });
    });

    return errors;
  };

  const handleSelectApprover = async () => {
    const dataToValidate =
      previewData.length > 0 ? previewData : getCurrentData();
    const errors = validatePreviewData(dataToValidate);
    setValidationErrors(errors);
    if (errors.length > 0) {
      setUploadError(
        `Please fix ${errors.length} validation error(s) before selecting an approver.`
      );
      return;
    }

    if (selectedType === "upload_account_team_members") {
      const csvFile = createCSVFileFromData(dataToValidate);
      try {
        const validationResult = await dispatch(
          validateAccountTeamCsv(csvFile)
        ).unwrap();
        const csvValidationErrors =
          buildAccountTeamCsvValidationErrors(validationResult);
        if (csvValidationErrors.length > 0) {
          setUploadError(csvValidationErrors);
          return;
        }
      } catch {
        setUploadError(
          "Unable to validate account team CSV. Please try again."
        );
        return;
      }
    }

    setUploadError(null);
    setShowSelectApprover(true);
  };

  const handleApproverSelect = (approverId: string) => {
    setSelectedApprover(approverId);
  };

  const getSelectedApproverDetails = () => {
    return availableApprovers.find(
      approver => approver.id === selectedApprover
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      title="Data Upload Manager"
      size="6xl"
    >
      <div className="p-0">
        {showConfirmation ? (
          <>
            {/* Confirmation Header */}
            <div className="pb-6 text-center">
              <div className="mb-5 inline-flex h-24 w-24 items-center justify-center rounded-full bg-violet-100 shadow-inner ring-4 ring-violet-100/50">
                <CheckCircle
                  size={48}
                  className="text-violet-600"
                  strokeWidth={2}
                />
              </div>
              <h3 className="mb-2 text-2xl font-bold tracking-tight text-gray-900">
                Ready to Submit for Review
              </h3>
              <div className="flex items-center justify-center gap-2 text-sm font-medium text-violet-600">
                <Users size={18} className="shrink-0" />
                {getConfirmationTypeTitle()}
              </div>
            </div>

            {/* Upload Summary Card */}
            <div className="mb-5 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <h4 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-500">
                Upload Summary
              </h4>
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="min-w-[100px] text-sm text-gray-500">
                    Upload Type
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {getUploadTypeDisplayName()}
                  </span>
                  <span className="ml-auto rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-semibold text-violet-700">
                    {getCurrentData().length} {getTypeLabel()}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="min-w-[100px] text-sm text-gray-500">
                    Batch Name
                  </span>
                  <span className="text-sm text-gray-900">{batchName}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="min-w-[100px] text-sm text-gray-500">
                    Submitted By
                  </span>
                  <span className="text-sm text-gray-900">{submittedBy}</span>
                </div>
              </div>
            </div>

            {/* What Happens Next */}
            <div className="mb-8 rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 to-white p-6 shadow-sm">
              <h4 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-700">
                What happens next
              </h4>
              <ol className="list-decimal space-y-3 pl-5 text-sm leading-relaxed text-gray-700">
                <li>
                  <strong className="font-semibold text-gray-900">
                    Review:
                  </strong>{" "}
                  Submitted to {getSelectedApproverDetails()?.name} (
                  {getSelectedApproverDetails()?.title}) for approval.
                </li>
                <li>
                  <strong className="font-semibold text-gray-900">
                    Processing:
                  </strong>{" "}
                  {getConfirmationProcessing()}
                </li>
                <li>
                  <strong className="font-semibold text-gray-900">
                    Outcome:
                  </strong>{" "}
                  {getConfirmationOutcome()}
                </li>
              </ol>
            </div>
          </>
        ) : showSelectApprover ? (
          <>
            {/* Select Approver Header */}
            <div className="pb-6 text-center">
              <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-violet-100 shadow-sm ring-2 ring-violet-100">
                <Users size={28} className="text-violet-600" />
              </div>
              <h3 className="mb-2 text-xl font-bold tracking-tight text-gray-900">
                Select Approver
              </h3>
              <p className="mx-auto text-sm leading-relaxed text-gray-500">
                Choose an Admin, C-Level, or Executive Leader to review and
                approve this upload of{" "}
                <span className="font-medium text-gray-700">
                  {getCurrentData().length} {getTypeLabel()}
                </span>
                .
              </p>
            </div>

            {/* Targeted Approval Process Alert */}
            <div className="mb-6 flex gap-3 rounded-xl border-l-4 border-violet-500 bg-violet-50/80 py-4 pl-4 pr-5">
              <div className="shrink-0">
                <AlertCircle size={20} className="text-violet-600" />
              </div>
              <div>
                <h4 className="mb-0.5 text-sm font-semibold text-violet-900">
                  Targeted Approval Process
                </h4>
                <p className="text-[13px] leading-relaxed text-violet-700">
                  Your selected approver will receive a notification to review
                  this upload. You can reassign to a different approver during
                  the review phase if needed.
                </p>
              </div>
            </div>

            {/* Available Approvers */}
            <div className="pb-6">
              <h4 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-500">
                Available approvers
              </h4>

              {approversLoading ? (
                <div className="flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-gray-50 py-12">
                  <Loader2 size={20} className="animate-spin text-violet-500" />
                  <span className="text-sm text-gray-500">
                    Loading approvers…
                  </span>
                </div>
              ) : approversError ? (
                <div className="rounded-xl border border-red-200 bg-red-50 p-5">
                  <p className="mb-3 text-sm text-red-700">
                    Error loading approvers: {approversError}
                  </p>
                  <Button
                    variant="secondary"
                    onClick={() => dispatch(fetchAdminUsers())}
                  >
                    Retry
                  </Button>
                </div>
              ) : availableApprovers.length === 0 ? (
                <div className="rounded-xl border border-gray-200 bg-gray-50 py-12 text-center text-sm text-gray-500">
                  No approvers available.
                </div>
              ) : (
                <>
                  {/* Search bar */}
                  <div
                    className={`relative mb-5 ${approverSearchQuery ? "[&_.search-input]:pr-9" : ""}`}
                  >
                    <SearchInput
                      placeholder="Search by name, email, or role…"
                      value={approverSearchQuery}
                      onChange={e => setApproverSearchQuery(e.target.value)}
                      rounded="normal"
                    />
                    {approverSearchQuery && (
                      <button
                        type="button"
                        onClick={() => setApproverSearchQuery("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-500/20"
                        aria-label="Clear search"
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>

                  <div className="max-h-[380px] space-y-2 overflow-y-auto rounded-xl border border-gray-200 bg-gray-50/50 p-3 shadow-sm">
                    {filteredApprovers.length === 0 ? (
                      <div className="rounded-lg bg-white py-12 text-center text-sm text-gray-500 shadow-sm">
                        No approvers match &quot;{approverSearchQuery.trim()}
                        &quot;. Try a different search.
                      </div>
                    ) : (
                      filteredApprovers.map(approver => (
                        <div
                          key={approver.id}
                          className={`flex cursor-pointer items-center justify-between gap-3 rounded-xl border px-4 py-3 transition-all duration-200 ${
                            selectedApprover === approver.id
                              ? "border-blue-500 bg-blue-50 shadow-md ring-2 ring-blue-500/20"
                              : "border-gray-200 bg-white shadow-sm hover:border-gray-300 hover:shadow"
                          }`}
                          onClick={() => handleApproverSelect(approver.id)}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-sm font-semibold text-gray-900">
                                {approver.name}
                              </span>
                              <span className="inline-flex rounded-full bg-emerald-600 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                                {approver.role}
                              </span>
                            </div>
                            <p className="mt-1 truncate text-xs text-gray-500">
                              {[approver.title, approver.email]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                          </div>
                          {selectedApprover === approver.id && (
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-500 shadow-sm">
                              <CheckCircle
                                size={18}
                                className="text-white"
                                strokeWidth={2.5}
                              />
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          </>
        ) : showPreview ? (
          <>
            {/* Preview Header */}
            <div className="flex flex-wrap items-center justify-between gap-4 pb-5">
              <div>
                <h3 className="m-0 text-lg font-bold tracking-tight text-gray-900">
                  Preview data
                </h3>
                <p className="mt-0.5 m-0 text-sm text-gray-500">
                  {getCurrentData().length} {getTypeLabel()} — review and edit
                  before saving.
                </p>
              </div>
              <button
                type="button"
                className="flex items-center gap-2 rounded-lg border border-violet-200 bg-white px-3 py-2 text-sm font-medium text-violet-600 shadow-sm transition-colors hover:bg-violet-50 hover:border-violet-300 focus:outline-none focus:ring-2 focus:ring-violet-500/20"
                onClick={handleBackToUpload}
              >
                <ArrowLeft size={18} className="shrink-0" strokeWidth={2} />
                Back to upload
              </button>
            </div>

            {/* Preview Data Section */}
            <div className="pb-6">
              {(() => {
                const currentData = getCurrentData();
                const columns = getOrderedColumnsForPreview(
                  currentData,
                  selectedType
                );

                return (
                  <>
                    {/* Data Table */}
                    <div className="mb-5 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                      <div className="max-h-[400px] overflow-auto">
                        <table className="min-w-[1200px] w-full border-collapse">
                          <thead className="sticky top-0 z-10 bg-gray-50">
                            <tr>
                              {columns.map(column => (
                                <th
                                  key={column}
                                  className="border-b border-gray-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
                                >
                                  {formatColumnName(column)}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {currentData.map((row, rowIndex) => (
                              <tr
                                key={rowIndex}
                                className="border-b border-gray-100 last:border-b-0 transition-colors hover:bg-gray-50/80"
                              >
                                {columns.map(column => {
                                  // Find the actual key in the row (prefer the column name as-is, then try conversions)
                                  let actualKey = column;
                                  if (row[column] === undefined) {
                                    if (column.includes("_")) {
                                      // Try camelCase version
                                      const camelCaseKey = column.replace(
                                        /_([a-z])/g,
                                        (_, letter) => letter.toUpperCase()
                                      );
                                      if (row[camelCaseKey] !== undefined) {
                                        actualKey = camelCaseKey;
                                      }
                                    } else {
                                      // Try snake_case version
                                      const snakeCaseKey = column
                                        .replace(/([A-Z])/g, "_$1")
                                        .toLowerCase();
                                      if (row[snakeCaseKey] !== undefined) {
                                        actualKey = snakeCaseKey;
                                      }
                                    }
                                  }

                                  const value = getRowValue(row, actualKey);
                                  return renderEditableCell(
                                    rowIndex,
                                    actualKey,
                                    value
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Validation Errors Summary */}
                    {validationErrors.length > 0 && (
                      <div className="mb-5 flex gap-3 rounded-xl border border-red-200 bg-red-50 p-4 shadow-sm">
                        <AlertCircle
                          size={18}
                          color="#ef4444"
                          className="mt-0.5 shrink-0"
                        />
                        <div className="flex-1">
                          <strong className="mb-1 block text-sm font-semibold text-red-600">
                            {validationErrors.length} validation error(s) found
                          </strong>
                          <p className="my-1 text-[13px] leading-snug text-red-800">
                            Please fix all errors before submitting. Errors are
                            highlighted in red in the table above.
                          </p>
                          <div className="mt-2 border-t border-red-200 pt-2">
                            {validationErrors.slice(0, 5).map((error, idx) => (
                              <div
                                key={idx}
                                className="relative mb-1 pl-3 text-xs text-red-800 before:absolute before:left-0 before:content-['•'] before:text-red-600"
                              >
                                Row {error.rowIndex + 1},{" "}
                                {formatColumnName(error.field)}: {error.message}
                              </div>
                            ))}
                            {validationErrors.length > 5 && (
                              <div className="relative pl-3 text-xs text-red-800 before:absolute before:left-0 before:content-['•'] before:text-red-600">
                                ... and {validationErrors.length - 5} more
                                error(s)
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Edited Cells Count */}
                    {editedCells.size > 0 && (
                      <div className="mb-4 flex gap-3 rounded-lg border border-violet-500 bg-violet-50 px-4 py-3">
                        <Pencil
                          size={18}
                          className="mt-0.5 shrink-0 text-violet-500"
                        />
                        <div className="flex-1">
                          <strong className="block text-sm text-violet-800">
                            {editedCells.size} cell(s) edited
                          </strong>
                          <p className="m-0 text-[13px] text-violet-800">
                            Changes will be applied when you continue to
                            confirm.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Batch Name Section */}
                    <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
                      <div className="mb-2 flex items-center gap-2">
                        <Pencil size={16} className="text-gray-500" />
                        <label className="text-sm font-medium text-gray-700">
                          Batch name{" "}
                          <span className="font-normal text-gray-500">
                            (optional)
                          </span>
                        </label>
                      </div>
                      <Input
                        name="batchName"
                        value={batchName}
                        onChange={e => setBatchName(e.target.value)}
                        placeholder={getDefaultBatchName()}
                        className="mb-2 w-full"
                      />
                      <p className="m-0 text-xs text-gray-500">
                        Give your upload a meaningful name to identify it later.
                        Leave empty to use a default.
                      </p>
                    </div>

                    {/* Upload Error Message */}
                    {uploadErrorMessages.length > 0 && (
                      <div className="my-5 flex gap-3 rounded-xl border border-red-200 bg-red-50 p-4 shadow-sm">
                        <AlertTriangle
                          size={20}
                          className="mt-0.5 shrink-0 text-red-600"
                        />
                        <div className="flex-1">
                          <h4 className="mb-1 text-sm font-semibold text-red-800">
                            Upload Errors
                          </h4>
                          <ul className="m-0 list-disc space-y-1 pl-4 text-[13px] leading-snug text-red-800">
                            {uploadErrorMessages.map((message, index) => (
                              <li key={`upload-error-${index}`}>{message}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </>
        ) : (
          <>
            {/* Upload Type Selection */}
            <div className="mb-8">
              <h3 className="mb-4 text-base font-semibold text-gray-900">
                What would you like to upload?
              </h3>
              <div className={`grid w-full gap-4 ${uploadTypeGridClass}`}>
                {uploadTypes.map(type => {
                  const isSelected = selectedType === type.id;
                  const selectedBorderClass =
                    type.id === "executives" ||
                    type.id === "upload_account_team_members"
                      ? "border-blue-500 bg-blue-50 shadow-lg shadow-blue-500/10 ring-2 ring-blue-500/20"
                      : type.id === "accounts"
                        ? "border-emerald-500 bg-emerald-50 shadow-lg shadow-emerald-500/10 ring-2 ring-emerald-500/20"
                        : "border-violet-500 bg-violet-50 shadow-lg shadow-violet-500/10 ring-2 ring-violet-500/20";

                  return (
                    <button
                      key={type.id}
                      type="button"
                      className={`flex h-full w-full min-w-0 flex-col items-center rounded-xl border-2 p-4 text-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:ring-offset-2 sm:p-5 ${
                        isSelected
                          ? selectedBorderClass
                          : "border-gray-200 bg-white shadow-sm hover:border-violet-300 hover:bg-gray-50 hover:shadow-md"
                      }`}
                      onClick={() => {
                        setUploadError(null);
                        setSelectedType(type.id);
                      }}
                    >
                      <span
                        className={`mb-4 flex h-12 w-12 items-center justify-center rounded-xl transition-colors ${
                          isSelected
                            ? type.id === "executives" ||
                              type.id === "upload_account_team_members"
                              ? "bg-blue-100 text-blue-600"
                              : type.id === "accounts"
                                ? "bg-emerald-100 text-emerald-600"
                                : "bg-violet-100 text-violet-600"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {type.id === "executives" && (
                          <Users size={26} strokeWidth={2} />
                        )}
                        {type.id === "accounts" && (
                          <Building2 size={26} strokeWidth={2} />
                        )}
                        {type.id === "composite" && (
                          <Database size={26} strokeWidth={2} />
                        )}
                        {type.id === "upload_account_team_members" && (
                          <Users size={26} strokeWidth={2} />
                        )}
                        {type.id === "upload_customer_team_members" && (
                          <UserRound size={26} strokeWidth={2} />
                        )}
                      </span>
                      <span className="mb-1 block text-base font-semibold text-gray-900">
                        {type.title}
                      </span>
                      <span className="text-sm leading-snug text-gray-500 text-pretty">
                        {type.description}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Account selection for team uploads */}
            {isTeamUploadType(selectedType) && (
              <div className="mb-8">
                <h3 className="mb-4 text-base font-semibold text-gray-900">
                  Select Accounts for this Upload
                </h3>
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="mb-3 flex flex-wrap items-center gap-3">
                    <div className="relative min-w-[220px] flex-1">
                      <SearchInput
                        placeholder="Search accounts by name..."
                        value={accountSearchQuery}
                        onChange={e => setAccountSearchQuery(e.target.value)}
                        rounded="normal"
                      />
                    </div>
                    <span className="text-sm text-gray-500 whitespace-nowrap">
                      {uploadAccounts.length} accounts |{" "}
                      {selectedAccountIds.length} selected
                    </span>
                  </div>

                  {accountsLoading ? (
                    <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
                      <Loader2
                        size={18}
                        className="animate-spin text-violet-500"
                      />
                      Loading accounts…
                    </div>
                  ) : accountsError ? (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                      {accountsError}
                      <Button
                        variant="secondary"
                        className="mt-3"
                        onClick={() => dispatch(fetchAccountsWithSponsors({}))}
                      >
                        Retry
                      </Button>
                    </div>
                  ) : (
                    <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-200">
                      {filteredUploadAccounts.length > 0 && (
                        <label className="flex cursor-pointer items-center gap-3 border-b border-gray-100 px-4 py-3 hover:bg-gray-50">
                          <CustomCheckbox
                            checked={
                              filteredUploadAccounts.length > 0 &&
                              filteredUploadAccounts.every(account =>
                                selectedAccountIds.includes(account.customer_id)
                              )
                            }
                            onChange={handleSelectAllAccounts}
                          />
                          <span className="text-sm font-medium text-gray-800">
                            Select All
                          </span>
                        </label>
                      )}
                      {filteredUploadAccounts.length === 0 ? (
                        <p className="px-4 py-8 text-center text-sm text-gray-500">
                          No accounts match your search.
                        </p>
                      ) : (
                        filteredUploadAccounts.map(account => (
                          <label
                            key={account.customer_id}
                            className="flex cursor-pointer items-center gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0 hover:bg-gray-50"
                          >
                            <CustomCheckbox
                              checked={selectedAccountIds.includes(
                                account.customer_id
                              )}
                              onChange={() =>
                                handleAccountToggle(account.customer_id)
                              }
                            />
                            <span className="min-w-0 flex-1 text-sm text-gray-800">
                              <span className="font-medium">
                                {account.name}
                              </span>
                              <span className="ml-2 text-xs text-gray-400">
                                ({truncateAccountId(account.customer_id)})
                              </span>
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  )}

                  <div className="mt-3 flex items-center justify-between text-sm">
                    <span className="text-gray-600">
                      Selected {selectedAccountIds.length} account(s)
                    </span>
                    {selectedAccountIds.length > 0 && (
                      <button
                        type="button"
                        className="font-medium text-violet-600 hover:text-violet-700"
                        onClick={handleClearSelectedAccounts}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Template Download Section */}
            <div className="mb-8">
              <div
                className={`flex gap-4 rounded-xl border p-5 shadow-sm ${
                  isTeamUploadType(selectedType)
                    ? "border-amber-200 bg-gradient-to-r from-amber-50 to-white"
                    : "border-violet-200 bg-gradient-to-r from-violet-50 to-white"
                }`}
              >
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                    isTeamUploadType(selectedType)
                      ? "bg-amber-100"
                      : "bg-violet-100"
                  }`}
                >
                  <FileText
                    size={20}
                    className={
                      isTeamUploadType(selectedType)
                        ? "text-amber-600"
                        : "text-violet-600"
                    }
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h4
                    className={`mb-1 text-sm font-semibold ${
                      isTeamUploadType(selectedType)
                        ? "text-amber-900"
                        : "text-violet-900"
                    }`}
                  >
                    Download Template First
                  </h4>
                  <p
                    className={`mb-4 text-sm leading-relaxed ${
                      isTeamUploadType(selectedType)
                        ? "text-amber-800"
                        : "text-violet-700"
                    }`}
                  >
                    {isTeamUploadType(selectedType) ? (
                      hasSelectedAccountsForTemplate ? (
                        <>
                          Download the CSV template for{" "}
                          <strong>{selectedAccountIds.length}</strong> selected
                          account
                          {selectedAccountIds.length === 1 ? "" : "s"}. Each row
                          includes account_id and account_name pre-filled for
                          your selection.
                          {selectedType === "upload_customer_team_members" ? (
                            <>
                              {" "}
                              Role must be one of:{" "}
                              {CUSTOMER_TEAM_ALLOWED_ROLES.join(", ")}.
                            </>
                          ) : null}
                        </>
                      ) : (
                        <>
                          Select at least one account above to download a
                          prefilled template with account_id and account_name,
                          or upload your own CSV below.
                        </>
                      )
                    ) : (
                      <>
                        Download the CSV template with the correct format and
                        sample data for {getUploadTypeDisplayName()}.
                      </>
                    )}
                  </p>
                  <Button
                    variant="secondary"
                    className={`inline-flex items-center gap-2 rounded-lg border bg-white px-4 py-2.5 text-sm font-medium shadow-sm transition-colors ${
                      isTeamUploadType(selectedType)
                        ? "border-amber-300 text-amber-800 hover:bg-amber-50 hover:border-amber-400"
                        : "border-violet-300 text-violet-700 hover:bg-violet-50 hover:border-violet-400"
                    }`}
                    onClick={handleDownloadTemplate}
                    disabled={
                      isTeamUploadType(selectedType) &&
                      selectedAccountIds.length === 0
                    }
                    title={
                      isTeamUploadType(selectedType) &&
                      selectedAccountIds.length === 0
                        ? "Select at least one account to download the template"
                        : undefined
                    }
                  >
                    <Download size={18} strokeWidth={2} />
                    Download {getUploadTypeDisplayName()} Template
                    {hasSelectedAccountsForTemplate &&
                      ` (${selectedAccountIds.length})`}
                  </Button>
                </div>
              </div>
            </div>

            {/* File Upload Section */}
            <div className="mb-8">
              <div
                className="relative rounded-xl border-2 border-dashed border-gray-300 bg-gray-50/80 py-14 px-6 text-center transition-all duration-200 group hover:border-violet-400 hover:bg-violet-50/50"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <div className="mb-4 flex justify-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-200/80 text-gray-500 transition-colors group-hover:bg-violet-100 group-hover:text-violet-500">
                    <Upload size={32} strokeWidth={2} />
                  </div>
                </div>
                <h4 className="mb-1 text-lg font-semibold text-gray-800">
                  Upload your CSV file
                </h4>
                <p className="mb-6 text-sm text-gray-500">
                  CSV only. Use the template format above.
                </p>
                <input
                  key={selectedType}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="csv-upload"
                />
                <label
                  htmlFor="csv-upload"
                  className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:bg-violet-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
                >
                  <Upload size={20} strokeWidth={2} />
                  Choose file
                </label>
                {selectedFile && (
                  <div className="mt-5 flex items-center justify-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-4 py-3 text-sm font-medium text-violet-800">
                    <FileText size={18} className="shrink-0 text-violet-600" />
                    <span className="truncate">{selectedFile.name}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Upload Error Message */}
            {uploadErrorMessages.length > 0 && (
              <div className="flex gap-3 rounded-xl border border-red-200 bg-red-50 p-4 shadow-sm">
                <AlertTriangle
                  size={20}
                  className="mt-0.5 shrink-0 text-red-600"
                />
                <div className="min-w-0 flex-1">
                  <h4 className="mb-2 text-sm font-semibold text-red-800">
                    Upload errors
                  </h4>
                  <ul className="m-0 list-disc space-y-1 pl-4 text-[13px] leading-relaxed text-red-800">
                    {uploadErrorMessages.map((message, index) => (
                      <li key={`upload-error-${index}`}>{message}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </>
        )}

        {/* Action Buttons */}
        {(showConfirmation || showSelectApprover || showPreview) && (
          <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50/80 px-6 py-5 sm:flex-row sm:justify-end sm:rounded-b-xl">
            {showConfirmation ? (
              <>
                <Button
                  variant="outline-primary"
                  onClick={handleBackToApprover}
                  disabled={isBulkUploading}
                >
                  Back to Approver
                </Button>
                <Button
                  variant="primary"
                  className="inline-flex items-center gap-2"
                  onClick={handleSubmitForReview}
                  disabled={isBulkUploading}
                >
                  {isBulkUploading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Save size={18} className="shrink-0" strokeWidth={2} />
                      Submit {getCurrentData().length} {getTypeLabel()} for
                      Review
                    </>
                  )}
                </Button>
              </>
            ) : showSelectApprover ? (
              <>
                <Button variant="outline-primary" onClick={handleBackToPreview}>
                  Back to Preview
                </Button>
                <Button
                  variant="primary"
                  className="inline-flex items-center gap-2"
                  onClick={handleContinueToConfirm}
                  disabled={!selectedApprover}
                >
                  Continue to confirm
                </Button>
              </>
            ) : showPreview ? (
              <Button
                variant="primary"
                className="flex items-center gap-1"
                onClick={handleSelectApprover}
                loading={accountTeamCsvValidating}
                disabled={
                  validationErrors.length > 0 || accountTeamCsvValidating
                }
                title={
                  validationErrors.length > 0
                    ? `Please fix ${validationErrors.length} validation error(s) before continuing`
                    : undefined
                }
              >
                <Users size={20} className="shrink-0" />
                Select approver
              </Button>
            ) : null}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DataUploadManager;
